import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ZoomTable, ZoomBtn, ZoomBadge, ZoomModal } from './zoom/ZoomTable';

/* ============================================================================
   DID Inventory — cross-source number registry (super_admin only, phase 1 read-only).
   Reads the PBX did_numbers table, the AudioCodes gateways' Tel-to-IP tables and
   (when configured) the esbc2 SBC API, then shows where every number really
   terminates versus where the PBX expects it.
   ============================================================================ */

type Source = {
  source_key: string; name: string; source_type: string; host: string; is_enabled: number;
  last_run_at: string | null; last_status: string | null; last_error: string | null; last_count: number | null;
};
type Summary = {
  running: boolean; lastRunStarted: string | null; lastRunFinished: string | null; lastError: string | null;
  counts: Record<string, number>; total: number; sources: Source[];
};
type Row = {
  id: number; did_e164: string; pbx_did_id: number | null; tenant_id: number | null; tenant_name: string | null;
  trunk_name: string | null; provider_name: string | null; routing_type: string | null; destination_value: string | null;
  extension_number: string | null; user_display: string | null; is_active: number | null;
  expected_source: string | null; observed_sources: { source: string; routes_to: string; detail: any }[];
  state: string; state_detail: string; last_reconciled_at: string | null;
};
type Rule = { rule_index: number; pattern: string; pattern_kind: string; local_pattern: string; dest: string | null; dest_kind: string; number_count: number; detail: any };

const STATE_META: Record<string, { label: string; tone: 'green' | 'red' | 'blue' | 'gray' | 'amber'; help: string }> = {
  in_sync:            { label: 'In sync',            tone: 'green', help: 'PBX destination set and the delivering source routes the number to the PBX.' },
  missing_on_source:  { label: 'Missing on source',  tone: 'red',   help: 'PBX has the number but the gateway/SBC has no rule delivering it — calls never arrive.' },
  misrouted:          { label: 'Misrouted',          tone: 'red',   help: 'Source has a rule for the number but sends it somewhere other than the PBX.' },
  orphan_on_source:   { label: 'Orphan on source',   tone: 'amber', help: 'Source delivers the number to the PBX but the PBX has no (active) DID row.' },
  unrouted_on_pbx:    { label: 'Unrouted on PBX',    tone: 'amber', help: 'Number arrives at the PBX but its DID row has no destination.' },
  conflict:           { label: 'Conflict',           tone: 'amber', help: 'More than one source delivers this number to the PBX.' },
  source_unreachable: { label: 'Source unreachable', tone: 'gray',  help: 'The delivering source could not be scanned; state unknown.' },
  unverified:         { label: 'Unverified',         tone: 'gray',  help: 'Delivering source is not configured for scanning yet.' },
  no_collector:       { label: 'No collector',       tone: 'gray',  help: 'The DID trunk is not mapped to any inventory source.' },
  external:           { label: 'External',           tone: 'blue',  help: 'Seen on a source but terminates at another customer/system, not this PBX.' },
  inactive:           { label: 'Inactive',           tone: 'gray',  help: 'DID row is inactive on the PBX.' },
};
const PROBLEM_STATES = ['missing_on_source', 'misrouted', 'orphan_on_source', 'unrouted_on_pbx', 'conflict'];

const fmtTime = (s?: string | null) => (s ? new Date(s).toLocaleString() : '—');
const stateBadge = (s: string) => { const m = STATE_META[s] || { label: s, tone: 'gray' as const }; return <ZoomBadge text={m.label} tone={m.tone} />; };
const srcShort = (k: string) => k.replace(/^gw-/, 'GW ').replace('pbx-tecducc', 'PBX');

export default function DidInventoryPage() {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState<'Numbers' | 'Source rules'>('Numbers');
  const [stateFilter, setStateFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [tenantFilter, setTenantFilter] = useState('');
  const [search, setSearch] = useState('');
  const [detailRow, setDetailRow] = useState<Row | null>(null);
  const [rulesSource, setRulesSource] = useState('gw-dubai');
  const [rules, setRules] = useState<{ source: any; rules: Rule[] } | null>(null);
  const [rulesLoading, setRulesLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, r] = await Promise.all([
        axios.get('/api/did-inventory/summary'),
        axios.get('/api/did-inventory', { params: { limit: 5000 } }),
      ]);
      setSummary(s.data); setRows(r.data.rows || []); setErr('');
    } catch (e: any) {
      setErr(e?.response?.data?.error || 'Failed to load DID inventory');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    // Poll faster while a scan is running so the operator sees results land.
    const t = setInterval(load, summary?.running ? 4000 : 60000);
    return () => clearInterval(t);
  }, [load, summary?.running]);

  const loadRules = useCallback(async (key: string) => {
    setRulesLoading(true);
    try { setRules((await axios.get(`/api/did-inventory/sources/${encodeURIComponent(key)}/rules`)).data); }
    catch (e: any) { setErr(e?.response?.data?.error || 'Failed to load rules'); }
    finally { setRulesLoading(false); }
  }, []);
  useEffect(() => { if (tab === 'Source rules') loadRules(rulesSource); }, [tab, rulesSource, loadRules]);

  const rescan = async (source?: string) => {
    try { await axios.post('/api/did-inventory/rescan', source ? { source } : {}); setTimeout(load, 1500); }
    catch (e: any) { setErr(e?.response?.data?.error || 'Rescan failed'); }
  };

  const tenants = useMemo(() => {
    const m = new Map<number, string>();
    rows.forEach((r) => { if (r.tenant_id) m.set(r.tenant_id, r.tenant_name || String(r.tenant_id)); });
    return Array.from(m.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (stateFilter === '__problems' ? !PROBLEM_STATES.includes(r.state) : (stateFilter && r.state !== stateFilter)) return false;
      if (sourceFilter && r.expected_source !== sourceFilter && !(r.observed_sources || []).some((o) => o.source === sourceFilter)) return false;
      if (tenantFilter && String(r.tenant_id) !== tenantFilter) return false;
      if (q && !(`${r.did_e164} ${r.tenant_name || ''} ${r.user_display || ''} ${r.extension_number || ''} ${r.trunk_name || ''} ${r.state_detail || ''}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [rows, stateFilter, sourceFilter, tenantFilter, search]);

  const counts = summary?.counts || {};
  const problems = PROBLEM_STATES.reduce((n, s) => n + (counts[s] || 0), 0);

  const columns = [
    { key: 'did_e164', label: 'Number', width: '150px', render: (r: Row) => <strong>{r.did_e164}</strong> },
    { key: 'tenant_name', label: 'Tenant', render: (r: Row) => r.tenant_name || <span className="admin-muted">—</span> },
    { key: 'destination', label: 'Destination', render: (r: Row) => r.pbx_did_id
        ? (r.destination_value
            ? <span>{r.routing_type !== 'extension' ? `${r.routing_type}: ` : ''}{r.destination_value}{r.user_display ? <span className="admin-muted"> · {r.user_display}</span> : null}</span>
            : <span className="admin-muted">no destination</span>)
        : <span className="admin-muted">not on PBX</span> },
    { key: 'expected_source', label: 'Expected via', render: (r: Row) => r.expected_source ? srcShort(r.expected_source) : <span className="admin-muted">{r.trunk_name || '—'}</span> },
    { key: 'observed', label: 'Observed on', render: (r: Row) => (r.observed_sources || []).length
        ? (r.observed_sources || []).map((o) => (
            <span key={o.source} title={o.detail?.pattern ? `rule ${o.detail.rule_index}: ${o.detail.pattern} → ${o.detail.dest || o.routes_to}` : (o.detail?.customer || o.routes_to)} style={{ marginRight: 4, display: 'inline-block' }}>
              <ZoomBadge text={`${srcShort(o.source)} → ${o.routes_to}`} tone={o.routes_to === 'pbx' ? 'green' : o.routes_to === 'self' ? 'red' : 'gray'} />
            </span>))
        : <span className="admin-muted">not seen</span> },
    { key: 'state', label: 'State', width: '150px', render: (r: Row) => stateBadge(r.state) },
    { key: 'state_detail', label: 'Detail', render: (r: Row) => <span className="admin-muted" style={{ fontSize: 12 }}>{r.state_detail}</span> },
  ];

  return (
    <>
      <div className="admin-page-title">DID Inventory</div>
      <div className="admin-page-subtitle">
        Every number the platform knows about, reconciled across the PBX DID table, the AudioCodes gateways' Tel-to-IP
        routing and the esbc2 SBC. Read-only in this phase: nothing here changes any source.
      </div>

      <div className="dashboard-grid">
        <StatCard title="Numbers tracked" value={summary?.total ?? 0} onClick={() => setStateFilter('')} active={stateFilter === ''} />
        <StatCard title="In sync" value={counts.in_sync || 0} onClick={() => setStateFilter('in_sync')} active={stateFilter === 'in_sync'} />
        <StatCard title="Problems" value={problems} danger={problems > 0} onClick={() => setStateFilter('__problems')} active={stateFilter === '__problems'} />
        <StatCard title="Missing on source" value={counts.missing_on_source || 0} danger={(counts.missing_on_source || 0) > 0} onClick={() => setStateFilter('missing_on_source')} active={stateFilter === 'missing_on_source'} />
        <StatCard title="Unrouted on PBX" value={counts.unrouted_on_pbx || 0} danger={(counts.unrouted_on_pbx || 0) > 0} onClick={() => setStateFilter('unrouted_on_pbx')} active={stateFilter === 'unrouted_on_pbx'} />
        <StatCard title="Orphans / misrouted" value={(counts.orphan_on_source || 0) + (counts.misrouted || 0)} danger={((counts.orphan_on_source || 0) + (counts.misrouted || 0)) > 0} onClick={() => setStateFilter('misrouted')} active={stateFilter === 'misrouted'} />
      </div>

      <div className="content-card">
        <div className="admin-card-header">
          <h3>Sources{summary?.running ? '  ·  scanning…' : summary?.lastRunFinished ? `  ·  last scan ${fmtTime(summary.lastRunFinished)}` : ''}</h3>
          <div>
            <button className="secondary-btn" onClick={load} style={{ marginRight: 8 }}>Refresh</button>
            <button className="primary-btn" onClick={() => rescan()} disabled={!!summary?.running}>{summary?.running ? 'Scanning…' : 'Rescan all'}</button>
          </div>
        </div>
        {err && <p className="admin-muted" style={{ color: '#dc2626' }}>{err}</p>}
        {summary?.lastError && <p className="admin-muted" style={{ color: '#dc2626' }}>Last run error: {summary.lastError}</p>}
        <table className="admin-table">
          <thead><tr><th>Source</th><th>Type</th><th>Host</th><th>Status</th><th>Numbers</th><th>Last scan</th><th></th></tr></thead>
          <tbody>
            {(summary?.sources || []).map((s) => (
              <tr key={s.source_key}>
                <td><strong>{s.name}</strong><div className="admin-muted" style={{ fontSize: 11 }}>{s.source_key}</div></td>
                <td>{s.source_type}</td>
                <td>{s.host}</td>
                <td>
                  <ZoomBadge text={s.last_status || 'never'} tone={s.last_status === 'ok' ? 'green' : s.last_status === 'error' ? 'red' : 'gray'} />
                  {s.last_error && <div className="admin-muted" style={{ fontSize: 11, maxWidth: 360 }}>{s.last_error}</div>}
                </td>
                <td>{s.last_count ?? '—'}</td>
                <td className="admin-muted">{fmtTime(s.last_run_at)}</td>
                <td><button className="secondary-btn" onClick={() => rescan(s.source_key)} disabled={!!summary?.running || !s.is_enabled}>Rescan</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="z-tbl-tabs" style={{ marginTop: 8 }}>
        {(['Numbers', 'Source rules'] as const).map((t) => (
          <button key={t} className={`z-tbl-tab${t === tab ? ' active' : ''}`} onClick={() => setTab(t)}>{t}</button>
        ))}
      </div>

      {tab === 'Numbers' && (
        <ZoomTable
          title=""
          search={search}
          onSearch={setSearch}
          searchPlaceholder="Search number, tenant, user, extension…"
          actions={(
            <>
              <select className="z-inline-select" value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}>
                <option value="">All states</option>
                <option value="__problems">Problems only</option>
                {Object.entries(STATE_META).map(([k, m]) => <option key={k} value={k}>{m.label} ({counts[k] || 0})</option>)}
              </select>
              <select className="z-inline-select" value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
                <option value="">All sources</option>
                {(summary?.sources || []).map((s) => <option key={s.source_key} value={s.source_key}>{s.name}</option>)}
              </select>
              <select className="z-inline-select" value={tenantFilter} onChange={(e) => setTenantFilter(e.target.value)}>
                <option value="">All tenants</option>
                {tenants.map(([id, name]) => <option key={id} value={String(id)}>{name}</option>)}
              </select>
            </>
          )}
          columns={columns}
          rows={filtered}
          rowKey={(r: Row) => r.did_e164}
          rowActions={(r: Row) => <ZoomBtn variant="default" onClick={() => setDetailRow(r)}>Details</ZoomBtn>}
          selectable={false}
          emptyText={loading ? 'Loading…' : 'No numbers match'}
          initialPageSize={25}
        />
      )}

      {tab === 'Source rules' && (
        <div className="content-card">
          <div className="admin-card-header">
            <h3>Routing rules as read from the source</h3>
            <select className="z-inline-select" value={rulesSource} onChange={(e) => setRulesSource(e.target.value)}>
              {(summary?.sources || []).filter((s) => s.source_type === 'gateway').map((s) => <option key={s.source_key} value={s.source_key}>{s.name}</option>)}
            </select>
          </div>
          <p className="admin-muted" style={{ fontSize: 12 }}>
            Tel-to-IP table in evaluation order (first match wins). Anything marked <em>self</em> loops back to the gateway and
            <em> catchall</em> rows decide where unlisted numbers go. Last read: {fmtTime(rules?.source?.last_run_at)}.
          </p>
          {rulesLoading && <p className="admin-muted">Loading…</p>}
          {rules && (
            <table className="admin-table">
              <thead><tr><th>#</th><th>Pattern</th><th>Kind</th><th>Numbers</th><th>Destination</th><th>Via</th><th>Src trunk grp</th><th>Transport</th></tr></thead>
              <tbody>
                {rules.rules.map((r) => (
                  <tr key={r.rule_index} style={r.dest_kind === 'self' ? { background: '#fef2f2' } : undefined}>
                    <td>{r.rule_index}</td>
                    <td><code>{r.pattern}</code></td>
                    <td>{r.pattern_kind}</td>
                    <td>{r.number_count || (r.pattern_kind === 'prefix' ? 'prefix' : r.pattern_kind === 'catchall' ? 'all' : '—')}</td>
                    <td><ZoomBadge text={`${r.dest_kind}${r.dest ? ' ' + r.dest : ''}`} tone={r.dest_kind === 'pbx' ? 'green' : r.dest_kind === 'self' ? 'red' : r.dest_kind === 'other' ? 'blue' : 'gray'} /></td>
                    <td className="admin-muted">{r.detail?.via || r.detail?.address || '—'}</td>
                    <td>{r.detail?.src_trunk_group ?? '—'}</td>
                    <td>{r.detail?.transport || '—'}</td>
                  </tr>
                ))}
                {rules.rules.length === 0 && <tr><td colSpan={8} className="admin-muted">No rules read yet — run a scan.</td></tr>}
              </tbody>
            </table>
          )}
        </div>
      )}

      {detailRow && (
        <ZoomModal title={`Number ${detailRow.did_e164}`} onClose={() => setDetailRow(null)}
          footer={<ZoomBtn variant="default" onClick={() => setDetailRow(null)}>Close</ZoomBtn>}>
          <div style={{ marginBottom: 10 }}>{stateBadge(detailRow.state)} <span className="admin-muted" style={{ fontSize: 12 }}>{STATE_META[detailRow.state]?.help}</span></div>
          <p style={{ fontSize: 13 }}>{detailRow.state_detail}</p>
          <table className="admin-table">
            <tbody>
              <tr><th style={{ width: 160 }}>PBX DID row</th><td>{detailRow.pbx_did_id ? `#${detailRow.pbx_did_id}` : 'none'}</td></tr>
              <tr><th>Tenant</th><td>{detailRow.tenant_name || '—'} {detailRow.tenant_id ? <span className="admin-muted">(id {detailRow.tenant_id})</span> : null}</td></tr>
              <tr><th>Routing</th><td>{detailRow.routing_type || '—'} → {detailRow.destination_value || <span className="admin-muted">none</span>}</td></tr>
              <tr><th>Extension / user</th><td>{detailRow.extension_number || '—'} {detailRow.user_display ? `· ${detailRow.user_display}` : ''}</td></tr>
              <tr><th>Trunk / provider</th><td>{detailRow.trunk_name || '—'} {detailRow.provider_name ? <span className="admin-muted">({detailRow.provider_name})</span> : null}</td></tr>
              <tr><th>Expected via</th><td>{detailRow.expected_source || '—'}</td></tr>
              <tr><th>Active on PBX</th><td>{detailRow.is_active === null ? '—' : detailRow.is_active ? 'yes' : 'no'}</td></tr>
              <tr><th>Reconciled</th><td className="admin-muted">{fmtTime(detailRow.last_reconciled_at)}</td></tr>
            </tbody>
          </table>
          <h4 style={{ margin: '14px 0 6px' }}>Observations per source</h4>
          {(detailRow.observed_sources || []).length === 0 && <p className="admin-muted">Not seen on any gateway or SBC.</p>}
          {(detailRow.observed_sources || []).map((o) => (
            <div key={o.source} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 10, marginBottom: 8 }}>
              <div><strong>{o.source}</strong> → <ZoomBadge text={o.routes_to} tone={o.routes_to === 'pbx' ? 'green' : o.routes_to === 'self' ? 'red' : 'gray'} /></div>
              <pre style={{ fontSize: 11, margin: '6px 0 0', whiteSpace: 'pre-wrap' }}>{JSON.stringify(o.detail, null, 2)}</pre>
            </div>
          ))}
        </ZoomModal>
      )}
    </>
  );
}

function StatCard({ title, value, danger, onClick, active }: { title: string; value: number; danger?: boolean; onClick?: () => void; active?: boolean }) {
  return (
    <div className="stat-card" onClick={onClick} style={{ cursor: onClick ? 'pointer' : undefined, outline: active ? '2px solid #2563eb' : undefined }}>
      <div className="stat-title">{title}</div>
      <div className="stat-value" style={danger ? { color: '#dc2626' } : undefined}>{value}</div>
    </div>
  );
}
